import { useTranslation } from 'react-i18next';
import { useMoney } from '@/lib/money/useMoney.js';
import type { AutoConfig, AdjustRule } from './useAutoBet.js';
import { SegmentedField } from '../fields/SegmentedField.js';
import { NumberField } from '../fields/NumberField.js';
import { minorToPlain } from '../../state/bet.js';

interface Props {
  value: AutoConfig;
  disabled?: boolean;
  onChange: (cfg: AutoConfig) => void;
}

export function AutoControls({ value, disabled, onChange }: Props) {
  const { t } = useTranslation('originals');
  const { toMinor, multiplier } = useMoney();
  const mult = multiplier();
  const set = (patch: Partial<AutoConfig>) => onChange({ ...value, ...patch });

  const parseStop = (text: string): bigint | null => {
    const s = text.trim();
    if (!s) return null;
    try {
      return toMinor(s);
    } catch {
      return null;
    }
  };
  const stopValue = (v: bigint | null) => (v == null ? '' : minorToPlain(v, mult));
  const ruleField = (key: 'onWin' | 'onLoss', rule: AdjustRule) => (
    <SegmentedField
      label={t(`auto.${key}`)}
      value={rule.mode}
      disabled={disabled}
      options={[
        { value: 'reset', label: t('auto.reset') },
        { value: 'increase', label: t('auto.increase') },
      ]}
      onChange={(mode) => set({ [key]: { ...rule, mode: mode as AdjustRule['mode'] } } as Partial<AutoConfig>)}
    />
  );

  const inputCls =
    'w-full h-11 px-3 rounded-xl bg-[var(--bg-elevated)] border border-[var(--border)] text-[14px] font-bold tabular-nums text-[var(--text-primary)] outline-none transition-colors placeholder:text-[var(--text-muted)] placeholder:font-medium focus:border-[var(--primary)] focus:shadow-[0_0_0_3px_var(--primary-glow)] disabled:opacity-40';
  const labelCls = 'text-[12px] font-medium text-[var(--text-secondary)]';

  return (
    <div className="flex flex-col gap-3">
      <label className="flex flex-col gap-1.5">
        <span className={labelCls}>{t('auto.rounds')}</span>
        <NumberField
          className={inputCls}
          disabled={disabled}
          value={value.numberOfBets}
          onChange={(n) => set({ numberOfBets: n })}
          placeholder={t('auto.infinite')}
        />
      </label>
      {ruleField('onWin', value.onWin)}
      {value.onWin.mode === 'increase' && (
        <label className="flex flex-col gap-1.5">
          <span className={labelCls}>{t('auto.increaseBy')}</span>
          <NumberField className={inputCls} disabled={disabled}
            value={value.onWin.pct} placeholder="0"
            onChange={(n) => set({ onWin: { ...value.onWin, pct: n } })} />
        </label>
      )}
      {ruleField('onLoss', value.onLoss)}
      {value.onLoss.mode === 'increase' && (
        <label className="flex flex-col gap-1.5">
          <span className={labelCls}>{t('auto.increaseBy')}</span>
          <NumberField className={inputCls} disabled={disabled}
            value={value.onLoss.pct} placeholder="0"
            onChange={(n) => set({ onLoss: { ...value.onLoss, pct: n } })} />
        </label>
      )}

      <div className="flex gap-2">
        <label className="flex-1 flex flex-col gap-1.5">
          <span className={labelCls}>{t('auto.stopProfit')}</span>
          <input
            className={inputCls}
            type="text"
            inputMode="decimal"
            disabled={disabled}
            defaultValue={stopValue(value.stopProfit)}
            placeholder="0"
            onChange={(e) => set({ stopProfit: parseStop(e.target.value) })}
          />
        </label>
        <label className="flex-1 flex flex-col gap-1.5">
          <span className={labelCls}>{t('auto.stopLoss')}</span>
          <input
            className={inputCls}
            type="text"
            inputMode="decimal"
            disabled={disabled}
            defaultValue={stopValue(value.stopLoss)}
            placeholder="0"
            onChange={(e) => set({ stopLoss: parseStop(e.target.value) })}
          />
        </label>
      </div>
    </div>
  );
}
